import { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import type { CargoBranch } from '@/data/cargo.api';

type DeliverySelectionMapProps = {
  branches: CargoBranch[];
  selectedBranchId?: string | null;
  previewBranchId?: string | null;
  onSelect: (branch: CargoBranch) => void;
  compact?: boolean;
};

function toNumber(value: number | string | undefined | null) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const DEFAULT_CENTER = { lat: 47.9184676, lng: 106.9177016 };

function buildMarkerSvg(fill: string, stroke: string) {
  return `data:image/svg+xml;utf8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="34" height="48" viewBox="0 0 34 48">
      <path d="M17 1C8.2 1 1 8.1 1 16.9C1 28.9 17 47 17 47C17 47 33 28.9 33 16.9C33 8.1 25.8 1 17 1Z" fill="${fill}" stroke="${stroke}" stroke-width="2"/>
      <circle cx="17" cy="17" r="6" fill="white"/>
    </svg>
  `)}`;
}

const defaultBranchIcon = L.icon({
  iconUrl: buildMarkerSvg('#3b82f6', '#1d4ed8'),
  shadowUrl: markerShadow,
  iconSize: [34, 48],
  iconAnchor: [17, 47],
  popupAnchor: [0, -38],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

const selectedBranchIcon = L.icon({
  iconUrl: buildMarkerSvg('#ef4444', '#b91c1c'),
  shadowUrl: markerShadow,
  iconSize: [38, 54],
  iconAnchor: [19, 53],
  popupAnchor: [0, -42],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
  className: 'drop-shadow-[0_0_12px_rgba(239,68,68,0.45)]',
});

type BranchPoint = {
  branch: CargoBranch;
  lat: number;
  lng: number;
};

function buildPopup(branch: CargoBranch) {
  const meta = [branch.city, branch.district].filter(Boolean).join(' • ');
  const image = branch.imageUrl
    ? `<img src="${branch.imageUrl}" alt="${branch.name}" style="width:100%;height:88px;object-fit:cover;border-radius:10px;margin-bottom:8px;" />`
    : '';
  return `
    <div style="min-width:180px">
      ${image}
      <div style="font-weight:600;margin-bottom:4px">${branch.name}</div>
      <div style="font-size:12px;color:#64748b">${meta}</div>
    </div>
  `;
}

export default function DeliverySelectionMap({
  branches,
  selectedBranchId,
  previewBranchId,
  onSelect,
  compact = false,
}: DeliverySelectionMapProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const resizeFrameRef = useRef<number | null>(null);
  const hasFitBoundsRef = useRef(false);
  const previousSelectedBranchIdRef = useRef<string | null>(null);
  const previousPreviewBranchIdRef = useRef<string | null>(null);

  const branchPoints = useMemo<BranchPoint[]>(
    () =>
      branches
        .map((branch) => {
          const lat = toNumber(branch.latitude);
          const lng = toNumber(branch.longitude);
          if (lat === null || lng === null) return null;
          return { branch, lat, lng };
        })
        .filter((point): point is BranchPoint => point !== null),
    [branches],
  );

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng],
      zoom: 11,
      scrollWheelZoom: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    markersLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    return () => {
      if (resizeFrameRef.current !== null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
      markersLayerRef.current?.clearLayers();
      markersLayerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const container = mapContainerRef.current;
    if (!map || !container) return;

    const invalidateMapSize = () => {
      if (resizeFrameRef.current !== null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
      }
      resizeFrameRef.current = window.requestAnimationFrame(() => {
        map.invalidateSize({ animate: false });
        resizeFrameRef.current = null;
      });
    };

    invalidateMapSize();
    const delayedInvalidate = window.setTimeout(invalidateMapSize, 240);
    const observer = new ResizeObserver(invalidateMapSize);
    observer.observe(container);

    return () => {
      observer.disconnect();
      window.clearTimeout(delayedInvalidate);
      if (resizeFrameRef.current !== null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
    };
  }, [compact]);

  useEffect(() => {
    const map = mapRef.current;
    const markersLayer = markersLayerRef.current;
    if (!map || !markersLayer) return;

    map.invalidateSize({ animate: false });

    markersLayer.clearLayers();

    if (branchPoints.length === 0) {
      map.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], 11);
      hasFitBoundsRef.current = false;
      return;
    }

    const bounds = L.latLngBounds(branchPoints.map((point) => [point.lat, point.lng] as [number, number]));

    branchPoints.forEach(({ branch, lat, lng }) => {
      const isSelected = branch.id === selectedBranchId;
      const isPreviewed = branch.id === previewBranchId;
      const marker = L.marker([lat, lng], {
        icon: isSelected ? selectedBranchIcon : defaultBranchIcon,
        riseOnHover: true,
        zIndexOffset: isSelected || isPreviewed ? 1000 : 0,
      });

      marker.bindPopup(buildPopup(branch));
      marker.on('click', () => onSelect(branch));

      if (isSelected || isPreviewed) {
        marker.openPopup();
      }

      marker.addTo(markersLayer);
    });

    const activeFocusId = previewBranchId ?? selectedBranchId;
    const focusedPoint = branchPoints.find((point) => point.branch.id === activeFocusId);
    if (focusedPoint) {
      const target = L.latLng(focusedPoint.lat, focusedPoint.lng);
      const boundsWithPadding = map.getBounds().pad(-0.18);
      const selectionChanged = previousSelectedBranchIdRef.current !== selectedBranchId;
      const previewChanged = previousPreviewBranchIdRef.current !== previewBranchId;

      if ((selectionChanged || previewChanged) && !boundsWithPadding.contains(target)) {
        map.flyTo(target, Math.max(map.getZoom(), 13), {
          animate: true,
          duration: previewBranchId ? 0.45 : 0.65,
        });
      }

      previousSelectedBranchIdRef.current = selectedBranchId ?? null;
      previousPreviewBranchIdRef.current = previewBranchId ?? null;
      return;
    }

    if (!hasFitBoundsRef.current) {
      map.fitBounds(bounds.pad(0.18), { animate: false });
      hasFitBoundsRef.current = true;
    }

    previousSelectedBranchIdRef.current = selectedBranchId ?? null;
    previousPreviewBranchIdRef.current = previewBranchId ?? null;
  }, [branchPoints, onSelect, previewBranchId, selectedBranchId]);

  return (
    <div>
      <div className="overflow-hidden rounded-2xl border bg-card shadow-sm dark:border-white/10 dark:bg-slate-950 dark:[&_.leaflet-container]:bg-slate-950 dark:[&_.leaflet-control-attribution]:bg-slate-950/75 dark:[&_.leaflet-control-attribution]:text-slate-300 dark:[&_.leaflet-control-zoom-in]:border-white/10 dark:[&_.leaflet-control-zoom-in]:bg-slate-900 dark:[&_.leaflet-control-zoom-in]:text-slate-100 dark:[&_.leaflet-control-zoom-out]:border-white/10 dark:[&_.leaflet-control-zoom-out]:bg-slate-900 dark:[&_.leaflet-control-zoom-out]:text-slate-100 dark:[&_.leaflet-popup-content-wrapper]:bg-slate-950 dark:[&_.leaflet-popup-content-wrapper]:text-slate-100 dark:[&_.leaflet-popup-tip]:bg-slate-950 dark:[&_.leaflet-tile]:brightness-[0.72] dark:[&_.leaflet-tile]:contrast-[1.18] dark:[&_.leaflet-tile]:saturate-[0.65]">
        <div ref={mapContainerRef} className={compact ? 'h-[260px] w-full' : 'h-[360px] w-full md:h-[440px] xl:h-[520px]'} />
      </div>
    </div>
  );
}
